import React, { useEffect, useMemo, useRef, useState } from "react";
import { IModalInstance } from "@whatslly/ui-components/modals";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DEFAULT_LAKE_SYNC_MINUTES, DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import { ButtonSet } from "@whatslly/ui-components/v2/BaseElements/ButtonSet";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import styles from "./Modals.module.scss";
import { useDependencyInjector, useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import { AWS_REGION_LIST } from "@whatslly/commons/utils";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { FiltersStore } from "../../../services/filtersStore";
import { DESTINATIONS_FILENAME_TEMPLATES } from "@whatslly/commons/destinationsFileNameTemplates";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import type { LakeState } from "@whatslly/types/messages-server/LakeState";
import { EncryptionKeyField } from "../components/EncryptionKeyField";
import { HideWhenLake, LakeField, lakeSettingsToSubmit, validateLake } from "../components/LakeField";
import { MediaFields } from "../components/MediaFields";

type FormatType = "json" | "csv" | "parquet";
type AWSRegionsType = (typeof AWS_REGION_LIST)[number];
type AvailableTabs = "access-key" | "idp";

const FILENAME_TEMPLATE = '{{orgId}}/{{sourcePhone}}/{{date timestamp "YYYY/MM/DD"}}.';
const FILENAME_TEMPLATE_OPTIONS = DESTINATIONS_FILENAME_TEMPLATES.map((template) => ({ label: template, value: template }));

const BASE_DATA = {
  type: "customS3" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
  settings: {
    format: "csv" as const,
    bucket: "",
    region: "us-east-2",
    filenameTemplate: `${FILENAME_TEMPLATE}csv`,
    mediaFilenameTemplate: "{{orgId}}/{{sourcePhone}}/media/{{_id}}.{{fileType}}",
    dek: "",
    lakeSyncMinutes: DEFAULT_LAKE_SYNC_MINUTES,
  },
};

type FormDataType = FormDestinationDataType<"customS3">;

// Shared form fields component
const SharedFormFields: React.FC<{
  onChangeFormat: (value: FormatType) => void;
  setRegionValue: (value: AWSRegionsType) => void;
  settings?: FormDataType;
}> = ({ onChangeFormat, setRegionValue, settings }) => {
  const { t } = useTranslation();

  return (
    <>
      <HideWhenLake>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.format")} required />
          <FormSelect<{ value: FormatType }>
            name="format"
            selectSize="small"
            paddingInline="none"
            options={[
              { label: "JSON", value: "json" },
              { label: "CSV", value: "csv" },
              { label: "Parquet", value: "parquet" },
            ]}
            onChange={(selectedOption: { value: FormatType }) => onChangeFormat(selectedOption?.value)}
            defaultValue={
              settings?.format
                ? {
                    label: settings.format.toUpperCase(),
                    value: settings.format,
                  }
                : { label: "CSV", value: "csv" }
            }
            isClearable={false}
            required
          />
        </div>
      </HideWhenLake>
      <div className={styles.fieldGroup}>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.bucket")} required />
          <FormTextInput name="bucket" paddingInline="none" inputSize="small" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.region")} required />
          <FormSelect<{ label: AWSRegionsType; value: AWSRegionsType }>
            selectSize="small"
            ignoreMenuDirection={true}
            isSearchable={true}
            name="region"
            paddingInline="none"
            onChange={(selectedOption: { label: AWSRegionsType; value: AWSRegionsType }) => setRegionValue(selectedOption?.value)}
            options={AWS_REGION_LIST.map((opt) => ({ label: opt, value: opt }))}
            defaultValue={
              settings?.region && AWS_REGION_LIST.find((opt) => opt === settings.region)
                ? { label: settings.region as AWSRegionsType, value: settings.region as AWSRegionsType }
                : { label: AWS_REGION_LIST[0], value: AWS_REGION_LIST[0] }
            }
            isClearable={false}
            required
          />
        </div>
      </div>
    </>
  );
};

// Base form wrapper with common logic
type AuthTabProps = IModalInstance<Destination> & { settings?: FormDataType; lake?: LakeState; destinationId?: string };

const BaseFormWrapper: React.FC<
  AuthTabProps & {
    children: React.ReactNode;
    initialValues?: FormDataType;
  }
> = ({ settings, lake, destinationId, closeModal, children, initialValues }) => {
  const [filenameTemplateSelect, setFilenameTemplateSelect] = useState<{ label: string; value: string } | undefined>();
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const [formatValue, setFormatValue] = useState<FormatType>(settings?.format ?? "csv");
  const [regionValue, setRegionValue] = useState<AWSRegionsType>(AWS_REGION_LIST.find((opt) => opt === settings?.region) ?? AWS_REGION_LIST[0]);
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const isSuperAdmin = useMemo(() => userStore.user?.isSuperAdmin || userStore.user?.isAdmin, []);

  const validate = async (values?: FormDataType) => {
    const errors: FormikErrors<Partial<FormDataType>> = {};
    if (!values?.region) errors.region = t("field_is_required");
    if (!values?.bucket) errors.bucket = t("field_is_required");
    if (!values?.lake) {
      if (!values?.format) errors.format = t("field_is_required");
      if (!values?.filenameTemplate) errors.filenameTemplate = t("field_is_required");
    }
    Object.assign(errors, validateLake(values ?? {}, t));

    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const onChangeFormat = (value: FormatType) => {
    setFormatValue(value);
    const currentFileNameTemplateValue = formikContext.current?.values.filenameTemplate || `${FILENAME_TEMPLATE}.${value}`;
    const [currentValue] = currentFileNameTemplateValue.split(".");
    const newFileNameTemplateValue = FILENAME_TEMPLATE_OPTIONS.find((opt) => {
      const [optValue, optFormat] = opt.value.split(".");
      return optValue === currentValue && optFormat === value;
    }) || {
      label: `${FILENAME_TEMPLATE}.${value}`,
      value: `${FILENAME_TEMPLATE}.${value}`,
    };
    setFilenameTemplateSelect(newFileNameTemplateValue);
    formikContext.current?.setFieldValue("filenameTemplate", newFileNameTemplateValue?.value || `${FILENAME_TEMPLATE}.${value}`);
  };

  const onFileNameTemplateChange = (value: string) => {
    const mediaTemplate = value?.startsWith("{{sourcePhone}}")
      ? "{{sourcePhone}}/media/{{_id}}.{{fileType}}"
      : "{{orgId}}/{{sourcePhone}}/media/{{_id}}.{{fileType}}";
    formikContext.current?.setFieldValue("mediaFilenameTemplate", mediaTemplate);
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    return { ...BASE_DATA, settings: lakeSettingsToSubmit({ ...data, format: formatValue, region: regionValue }) };
  };

  const onSubmit = async () => {
    store.testConnection(t, getFormValues(), destinationId);
    const res = await store.testConnectionPromise;
    if (res?.success) {
      closeModal(getFormValues());
    }
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, []);

  return (
    <FormElement initialValues={initialValues || settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
      <SharedFormFields onChangeFormat={onChangeFormat} setRegionValue={setRegionValue} settings={settings} />
      {children}
      <HideWhenLake>
        <div className={classNames(styles.field, { [styles.hidden]: !isSuperAdmin })}>
          <Label className={styles.filedLabel} text={t("destinations_page.file_name_template")} required />
          <Select
            selectSize="small"
            value={filenameTemplateSelect}
            options={FILENAME_TEMPLATE_OPTIONS.filter((opt) => {
              return opt.value.includes(formatValue ?? "csv");
            })}
            paddingInline="none"
            onChange={(selectedOption: (typeof FILENAME_TEMPLATE_OPTIONS)[number]) => {
              formikContext.current?.setFieldValue("filenameTemplate", selectedOption?.value);
              onFileNameTemplateChange(selectedOption?.value);
            }}
            defaultValue={
              FILENAME_TEMPLATE_OPTIONS.find((opt) =>
                settings?.filenameTemplate ? settings.filenameTemplate === opt.value : opt.value.includes(formatValue ?? "csv")
              ) ?? {
                label: `${FILENAME_TEMPLATE}.csv`,
                value: `${FILENAME_TEMPLATE}.csv`,
              }
            }
          />
          <div className={classNames(styles.field, styles.hidden)}>
            <FormTextInput name="filenameTemplate" paddingInline="none" inputSize="small" />
          </div>
        </div>
      </HideWhenLake>
      <MediaFields inputSize="small" />
      <EncryptionKeyField locked={Boolean(settings)} />
      <LakeField type="customS3" state={lake} isNewDestination={!settings} />
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
};

const AccessKeyAuthTab: React.FC<AuthTabProps> = ({ settings, lake, destinationId, closeModal }) => {
  const { t } = useTranslation();

  return (
    <BaseFormWrapper settings={settings} lake={lake} destinationId={destinationId} closeModal={closeModal}>
      <div className={styles.fieldGroup}>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.accesses_key_id")} />
          <FormTextInput name="accessKeyId" paddingInline="none" inputSize="small" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.secret_access_key")} />
          <FormPasswordTextInput name="secretAccessKey" paddingInline="none" inputSize="small" />
        </div>
      </div>
    </BaseFormWrapper>
  );
};

const IdentityProviderAuthTab: React.FC<AuthTabProps> = ({ settings, lake, destinationId, closeModal }) => {
  const { t } = useTranslation();
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const filtersStore = useDependencyInjector(FiltersStore);
  const resolvedOrgId = filtersStore.orgId || userStore.user?.organizationId;

  const externalId = useMemo<string>(() => {
    if (settings?.externalId) return settings.externalId;
    const randomHex = Math.random().toString(16).substring(2, 10).toUpperCase();
    return `app:${randomHex}:${resolvedOrgId}`;
  }, [settings?.externalId, resolvedOrgId]);

  const initialSettings = useMemo<FormDataType>(
    () => ({
      ...(settings || BASE_DATA.settings),
      externalId,
    }),
    [settings, externalId]
  );

  const providerUrl = useMemo(
    () => `https://api-${import.meta.env.VITE_API_DEFAULT_POD?.toString() || "us1"}.tuvis.com/message/destinations/${resolvedOrgId}/oidc`,
    [resolvedOrgId]
  );

  return (
    <>
      <p className={styles.description}>
        {t("destinations_page.openid_provider_url")}: <br />
        <a href={providerUrl} target={"_blank"}>
          {providerUrl}
        </a>
      </p>
      <BaseFormWrapper settings={settings} lake={lake} destinationId={destinationId} closeModal={closeModal} initialValues={initialSettings}>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.iam_role_name")} />
          <FormTextInput name="roleArn" paddingInline="none" inputSize="small" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.external_id")} />
          <FormTextInput name="externalId" paddingInline="none" disabled inputSize="small" />
        </div>
      </BaseFormWrapper>
    </>
  );
};

export const CustomS3ModalForm: React.FC<DestinationFormProps<"customS3">> = ({ settings, lake, destinationId, onSubmitForm }) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<AvailableTabs>(settings?.roleArn ? "idp" : "access-key");

  const TABS: {
    key: AvailableTabs;
    value: string;
    Component: any;
  }[] = useMemo(
    () => [
      {
        Component: AccessKeyAuthTab,
        key: "access-key",
        value: t("destinations_page.access_key_auth"),
        testId: "access-key-tab",
      },
      {
        Component: IdentityProviderAuthTab,
        key: "idp",
        value: t("destinations_page.identity_provider_auth"),
        testId: "idp-tab",
      },
    ],
    [t]
  );

  const activeTab = useMemo(() => TABS.find((item) => item.key === selectedTab) ?? TABS[0], [selectedTab, TABS]);

  return (
    <>
      <ButtonSet<{ key: AvailableTabs; value: string }>
        testId="switch-connect-mode"
        className={classNames(styles.tabs, styles.s3Tabs)}
        items={TABS}
        onClick={(item) => setSelectedTab(item.key)}
        activeItem={activeTab}
      />
      <activeTab.Component closeModal={onSubmitForm} settings={settings} lake={lake} destinationId={destinationId} />
    </>
  );
};
